import { useState, useEffect, ReactNode } from 'react';
import { View, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { SubHeading, Title } from '@/components/ui/Text';

type CollapsibleSectionProps = {
  title: string;
  children: ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  variant?: 'elevated' | 'outlined';
  rightAction?: ReactNode;
  icon?: keyof typeof Ionicons.glyphMap;
  /** Header icon color; defaults to primary. */
  iconColor?: string;
  /** Use compact title weight for plan-style section headers. */
  titleStyle?: 'section' | 'default';
};

export function CollapsibleSection({
  title,
  children,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandedChange,
  variant = 'outlined',
  rightAction,
  icon,
  iconColor,
  titleStyle = 'default',
}: CollapsibleSectionProps) {
  const theme = useTheme();
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  
  // Use controlled expanded if provided, otherwise use internal state
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;
  
  // Update internal state when controlled expanded changes
  useEffect(() => {
    if (isControlled && controlledExpanded !== undefined) {
      setInternalExpanded(controlledExpanded);
    }
  }, [controlledExpanded, isControlled]);
  
  const handleToggle = () => {
    const newExpanded = !expanded;
    if (isControlled) {
      onExpandedChange?.(newExpanded);
    } else {
      setInternalExpanded(newExpanded);
    }
  };

  return (
    <Card variant={variant} style={{ marginBottom: theme.spacing(2) }}>
      <Pressable
        onPress={handleToggle}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: expanded ? theme.spacing(1.5) : 0,
          paddingTop: theme.spacing(0.5),
          opacity: 1,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flex: 1 }}>
          {icon && (
            <Ionicons name={icon} size={20} color={iconColor ?? theme.colors.primary} />
          )}
          {titleStyle === 'section' ? (
            <Title style={{ fontWeight: '600' }}>{title}</Title>
          ) : (
            <SubHeading>{title}</SubHeading>
          )}
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
          {rightAction}
          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={20}
            color={theme.colors.textSecondary}
          />
        </View>
      </Pressable>
      {expanded && (
        <View style={{ marginTop: theme.spacing(1) }}>
          {children}
        </View>
      )}
    </Card>
  );
}

